import React, { Component, ReactNode } from 'react';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  isChunkError: boolean;
}

class LazyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk loading error
    const isChunkError =
      error.message?.includes('Loading chunk') ||
      error.message?.includes('ChunkLoadError') ||
      error.name === 'ChunkLoadError';

    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('LazyErrorBoundary caught an error:', error, errorInfo);

    // Log chunk errors for monitoring
    if (this.state.isChunkError) {
      console.warn('Chunk loading failed, this might be due to a deployment or network issue');
    }
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      // For chunk errors, reload the page to get fresh chunks
      window.location.reload();
    } else {
      // For other errors, just reset the error boundary
      this.setState({ hasError: false, error: null, isChunkError: false });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { componentName = 'Component' } = this.props;
      const { isChunkError, error } = this.state;

      return (
        <div className="flex min-h-0 min-w-0 w-full flex-col items-center justify-center border border-border-subtle bg-surface-container-low px-5 py-6 text-center sm:px-8 sm:py-8 rounded-lg">
          <div className="flex w-full min-w-0 max-w-2xl flex-col items-center gap-3">
          {isChunkError ? (
            <Download className="h-8 w-8 shrink-0 text-text-muted" />
          ) : (
            <AlertTriangle className="h-8 w-8 shrink-0 text-accent-warning" />
          )}

          <h5 className="text-sm font-medium text-text-secondary">
            {isChunkError ? `Unable to load ${componentName}` : `${componentName} Error`}
          </h5>

          <p className="text-sm text-text-muted">
            {isChunkError
              ? 'This might be due to a recent update. Refreshing will load the latest version.'
              : `An error occurred while rendering ${componentName}. You can try again or refresh the page.`
            }
          </p>

          {error && !isChunkError && (
            <div className="w-full min-w-0 rounded-md border border-accent-bearish/20 bg-accent-bearish/5 px-4 py-3 text-start text-sm font-mono text-accent-bearish break-words [overflow-wrap:anywhere]">
              {error.message}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-2">
            <button
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-accent-primary text-on-primary hover:bg-accent-primary-hover transition-colors"
              onClick={this.handleRetry}
            >
              <RefreshCw className="w-4 h-4" />
              {isChunkError ? 'Refresh Page' : 'Try Again'}
            </button>

            {!isChunkError && (
              <button
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm border border-border-subtle bg-transparent text-text-secondary hover:bg-surface-container transition-colors"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </button>
            )}
          </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LazyErrorBoundary;
